import React, { useState, useEffect } from 'react';
import Navbar from '../components/NavbarHome';
import Header from '../components/header';
import FormCard from '../components/Card';
import { Flex, ScrollArea, Group, Container, Text, Badge, Modal, Input, ActionIcon, Tooltip, TextInput, Button } from '@mantine/core';
import './home.css';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import surveyData from '../components/SurveyForm/templatesData';
import { useNavigate, useLocation } from 'react-router-dom';
import { FooterCentered } from '../components/Footer';
import { ToastMessages, TextMessages } from './messages/homeTexts';
import { URLs } from './messages/apiUrls';
import { getAuthToken } from '../utils/auth';

export default function Home() {
  const navigate = useNavigate();
  const [templateOpen, setTemplateOpen] = useState(false);
  const [type, setType] = useState("All");
  const [parsedForms, setParsedForms] = useState([]);
  const [parsedTemplates, setParsedTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [link, setLink] = useState("");
  const [opened, { open, close, toggle }] = useDisclosure(false);
  const [navItem, setNavItem] = useState('My Forms');
  const [showIcon, setShowIcon] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sharedForms, setSharedForms] = useState([]);
  const [mailLoading, setMailLoading] = useState(false);
  
  function getTokenFromCookie() {
    return getAuthToken();
  }

  function getCookie(name) {
    const cookies = document.cookie.split(';');
    const cookie = cookies.find(c => c.trim().startsWith(`${name}=`));
    return cookie ? cookie.split('=')[1] : null;
  }

  const location = useLocation();

  useEffect(() => {
    if (location.state?.navItem) {
      setNavItem(location.state.navItem);
      toggleIconVisibility();
    }
  }, [location.state]);

  useEffect(() => {
    const token = getTokenFromCookie();

    if (!token) {
      window.location.href = '/login';
    } else {
      const initializeData = async () => {
        try {
          setIsLoading(true);
          await fetchData();
          await fetchTemplate();
          toggleIconVisibility();
        } catch (error) {
          console.error("Error initializing data:", error);
          toast.error(ToastMessages.errorGeneral || 'Error initializing data');
        } finally {
          setIsLoading(false);
        }
      };

      initializeData();
    }
  }, []);

  useEffect(() => {
    if (navItem === 'Shared With Me') {
      fetchSharedForms();
    }
  }, [navItem]);

  // Function to move a form to the top of parsedForms
  const moveFormToTop = (formId) => {
    setParsedForms((prevForms) => {
      const index = prevForms.findIndex(form => form.id === formId);
      if (index === -1) return prevForms;
      const form = prevForms[index];
      return [form, ...prevForms.slice(0, index), ...prevForms.slice(index + 1)];
    });
  };

  const fetchData = async () => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.GET_FORM, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.formData) {
            const parsedForms = data.formData.map(form => ({
              ...form,
              formData: typeof form.formData === 'string' ? JSON.parse(form.formData) : form.formData,
            }));
            setParsedForms(parsedForms);
          } else {
            console.error('Invalid data format:', data);
            toast.error(ToastMessages.invalidDataFormat || 'Invalid data format');
          }
        } else {
          console.error('Error fetching forms:', await response.text());
          toast.error(ToastMessages.formRetrieveFailed || 'Error fetching forms');
        }
      } catch (error) {
        console.error('Error fetching forms:', error);
        toast.error(ToastMessages.formRetrieveFailed || 'Error fetching forms');
      }
    }
  };

  const fetchTemplate = async () => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.GET_TEMPLATES, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: getCookie('email') }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.templateData) {
            const parsedTemplate = data.templateData.map(template => ({
              ...template,
              TemplateData: typeof template.TemplateData === 'string' ? JSON.parse(template.TemplateData) : template.TemplateData,
            }));
            setParsedTemplates(parsedTemplate);
          } else {
            console.error('Error fetching templates:', data);
            toast.error(ToastMessages.templateRetrieveFailed || 'Invalid template data format');
          }
        } else {
          console.error('Error fetching templates:', await response.text());
          toast.error(ToastMessages.templateRetrieveFailed || 'Error fetching templates');
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast.error(ToastMessages.templateRetrieveFailed || 'Error fetching templates');
      }
    }
  };

  const handleDelete = async (id) => {
    const token = getTokenFromCookie();
    const endpoint = navItem === 'Trash' ? 'permenantdeleteForm' : 'deleteForm';
    const url = `${process.env.REACT_APP_API_BASE_URL}/${endpoint}`;
    if (token) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.formDeletedSuccess);
          await fetchData();
        } else {
          toast.error(ToastMessages.formDeleteFailed);
        }
      } catch (error) {
        console.error('Error deleting form:', error);
        toast.error(ToastMessages.formDeleteFailed);
      }
    }
  };

  const archiveForm = async (id) => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.ARCHIVE_FORM, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.formArchivedSuccess);
          await fetchData();
        } else {
          toast.error(ToastMessages.formArchiveFailed);
        }
      } catch (error) {
        console.error('Error archiving form:', error);
        toast.error(ToastMessages.formArchiveFailed);
      }
    }
  };

  const archiveOff = async (id) => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.ARCHIVE_OFF_FORM, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.formUnarchivedSuccess);
          await fetchData();
          moveFormToTop(id); // Move unarchived form to top
        } else {
          toast.error(ToastMessages.formUnarchiveFailed);
        }
      } catch (error) {
        console.error('Error unarchiving form:', error);
        toast.error(ToastMessages.formUnarchiveFailed);
      }
    }
  };

  const handleSend = async (id) => {
    const form = parsedForms.find(form => form.id === id);
    if (form) {
      setLink(form.formLink);
      console.log('Opening modal with toggle:', toggle);
      toggle();
      moveFormToTop(id); // Move sent form to top
    } else {
      console.error('Form with ID', id, 'not found');
      toast.error(ToastMessages.formNotFound || "Form not found");
    }
  };

  const handleRestore = async (id) => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.RETRIEVE_FORM, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.formRetrievedSuccess);
          await fetchData();
          moveFormToTop(id); // Move restored form to top
        } else {
          toast.error(ToastMessages.formRetrieveFailed);
        }
      } catch (error) {
        console.error('Error retrieving form:', error);
        toast.error(ToastMessages.formRetrieveFailed);
      }
    }
  };

  const handleTemplateDelete = async (id) => {
    const token = getTokenFromCookie();
    const endpoint = navItem === 'Trash' ? 'PermenantDeleteTemplate' : 'DeleteTemplate';
    const url = `${process.env.REACT_APP_API_BASE_URL}/${endpoint}`;
    if (token) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.templateDeletedSuccess);
          await fetchTemplate();
        } else {
          toast.error(ToastMessages.templateDeleteFailed);
        }
      } catch (error) {
        console.error('Error deleting Template:', error);
        toast.error(ToastMessages.templateDeleteFailed);
      }
    }
  };

  const archiveTemplate = async (id) => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.ARCHIVE_TEMPLATE, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.templateArchivedSuccess);
          await fetchTemplate();
        } else {
          toast.error(ToastMessages.templateArchiveFailed);
        }
      } catch (error) {
        console.error('Error archiving template:', error);
        toast.error(ToastMessages.templateArchiveFailed);
      }
    }
  };

  const archiveOffTemplate = async (id) => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.ARCHIVE_OFF_TEMPLATE, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.templateUnarchivedSuccess);
          await fetchTemplate();
        } else {
          toast.error(ToastMessages.templateUnarchiveFailed);
        }
      } catch (error) {
        console.error('Error unarchiving template:', error);
        toast.error(ToastMessages.templateUnarchiveFailed);
      }
    }
  };

  const restoreTemplate = async (id) => {
    const token = getTokenFromCookie();
    if (token) {
      try {
        const response = await fetch(URLs.RESTORE_TEMPLATE, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id })
        });

        if (response.ok) {
          toast.success(ToastMessages.templateRestoredSuccess);
          await fetchTemplate();
        } else {
          toast.error(ToastMessages.templateRestoreFailed);
        }
      } catch (error) {
        console.error('Error restoring template:', error);
        toast.error(ToastMessages.templateRestoreFailed);
      }
    }
  };

  const sendMail = async (email) => {
    try {
      const form = parsedForms.find(form => form.formLink === link);
      if (!form) {
        toast.error("Form not found");
        return;
      }
      const formData = {
        formLink: link,
        formTitle: form.formName,
        recipientEmail: email,
        formType: form.type || 'open',
        endDate: form.dateRange && form.dateRange[1] ? form.dateRange[1] : null,
      };
      setMailLoading(true);
      console.log('Sending formData:', formData);
      const response = await fetch(URLs.SEND_FORM_LINK_MAIL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getTokenFromCookie()}`,
        },
        body: JSON.stringify({ formData }),
      });
      if (response.ok) {
        toast.success(ToastMessages.emailSentSuccess || "Email sent successfully");
        setEmailModalOpen(false);
        setRecipientEmail('');
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        toast.error(errorData.error || ToastMessages.emailSendFailed || "Failed to send email");
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(ToastMessages.emailSendFailed || "Failed to send email");
    } finally {
    setMailLoading(false);
    }
  };

  const CreateTemplate = async () => {
    navigate('/createTemplate');
  };

  const handleAddClick = () => {
    if (navItem === 'Templates') CreateTemplate();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleStartFromScratch = () => {
    window.localStorage.removeItem("survey-json");
    navigate('/build');
  };

  const handleChooseTemplate = () => {
    setTemplateOpen(true);
    setModalOpen(false);
  };

  const toggleIconVisibility = () => {
    if (navItem === 'Templates' || navItem === 'My Forms') {
      setShowIcon(true);
    } else {
      setShowIcon(false);
    }
  };

  // Handler for view and responses actions
  const handleFormAction = (id) => {
    moveFormToTop(id);
  };

  const fetchSharedForms = async () => {
  const token = getTokenFromCookie();
  const res = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/forms/shared-with-me`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  const data = await res.json();

  // normalize response
  const formsArray =
    Array.isArray(data)
      ? data
      : Array.isArray(data.sharedForms)
      ? data.sharedForms
      : Array.isArray(data.data)
      ? data.data
      : [];

  setSharedForms(formsArray);
};


  return (
    <div>
      {isLoading ? (
        <div style={{color: 'gray', fontSize: '25px', textAlign: 'center', marginTop: '20px'}}>Loading...</div>
      ) : (
        <div style={{ height: '100vh', backgroundColor: '#f7f6f5', position: "relative" }}>
          <Header />
          <Flex direction='row' wrap='nowrap'>
            <Navbar setNavItem={setNavItem} setTemplateOpen={setTemplateOpen} navItem={navItem} />
            {/*<Container
              pos={'absolute'}
              top={navItem === 'Trash' ? '100px' : '70px'}
              left={navItem === 'Trash' ? '20%' : '20%'}
              transform={'translateX(-50%)'}
              size={'xl'}
              m={'xs'}
              p={'10px 20px'}
              style={{
                zIndex: 2,
                backgroundColor: '#1ab394',
                color: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                fontWeight: 'bold',
                fontSize: '13px',
                textAlign: 'center',
                maxWidth: '150px',
                wordWrap: 'break-word',
              }}
            >
              <p style={{ margin: 0 }}>{navItem}</p>
            </Container>*/}

            {navItem === "My Forms" && (
              <Container
                pos={'absolute'}
                top={'70px'}
                right={'20px'}
                size={'xl'}
                m={'xs'}
                p={'0'}
                style={{ zIndex: 2 }}
              >
                <Group>
                  <Button  variant={type === "All" ? 'filled' : 'outline'} onClick={() => setType("All")}>All</Button>
                  <Button  variant={type === "Draft" ? 'filled' : 'outline'} onClick={() => setType("Draft")}>Draft</Button>
                  <Button  variant={type === "Saved" ? 'filled' : 'outline'} onClick={() => setType("Saved")}>Saved</Button>
                </Group>
              </Container>
            )}

            {navItem === "Templates" && (
              <Container
                pos={'absolute'}
                top={'70px'}
                right={'20px'}
                fluid
                size={'xl'}
                m={'xs'}
                p={'0'}
                style={{ zIndex: 10 }}
              >
                <Group>
                  <Button  variant={type === "All" ? 'filled' : 'outline'} onClick={() => setType("All")}>All</Button>
                  <Button  variant={type === "Draft" ? 'filled' : 'outline'} onClick={() => setType("Draft")}>Draft</Button>
                  <Button  variant={type === "Saved" ? 'filled' : 'outline'} onClick={() => setType("Saved")}>Saved</Button>
                </Group>
              </Container>
            )}

            {showIcon && (
              <Tooltip label={navItem === 'Templates' ? "Create Template" : TextMessages.chooseOption}>
                <ActionIcon
                  variant="filled"
                  size="xl"
                  style={{
                    position: 'fixed',
                    bottom: '65px',
                    right: '20px',
                    boxShadow: '2px 4px 4px rgba(0,0,0,0.1)',
                    zIndex: 150
                  }}
                  onClick={handleAddClick}
                >
                  <IconPlus size={24} />
                </ActionIcon>
              </Tooltip>
            )}

            <ScrollArea h="auto" bg='#f7f6f5' w='100%' pos={'relative'} pb={60}>
              {navItem === "Trash" && (
                <Container fluid bg={'gray'} size={'xl'} w='full' c='white' styles={{
                  root: {
                    width: '100%',
                    textAlign: 'center'
                  }
                }}>
                  <Text fw={700}>{TextMessages.trashWarning}</Text>
                </Container>
              )}

              <Flex
                direction='row'
                wrap='wrap'
                justify='center'
                align='center'
                styles={{
                  root: {
                    textAlign: 'center',
                    margin: 'auto',
                  },
                }}
              >
                {navItem === 'My Forms' && (
                  <>
                    {parsedForms.length === 0 ? (
                      <Container 
                      style={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            marginTop: '270px'
                          }}>
                        <p style={{
                          fontSize: '1.5rem',
                          color: '#333',
                          fontWeight: 'bold',
                          marginBottom: '1rem',
                          lineHeight: '1.4'
                        }}>
                          No forms available. <br />Click the <span style={{ color: '#1ab394' }}> "+" </span> icon to start creating your forms.
                        </p>
                      </Container>
                    ) : (
                      parsedForms.map(form => (
                        !form.isTrash && !form.isArchive && (
                          <Container pos={'relative'} mt={'50'} key={form.id}>
                            {/* {type !== "Saved" && form.isDraft && (
                              <Badge
                                //color="orange.4"
                                variant="filled"
                                pos={'absolute'}
                                left={'70%'}
                                top={'27%'}
                                styles={{ root: { zIndex: 100 } }}
                              >
                                Draft
                              </Badge>
                            )} */}
                            {type === "Draft" && form.isDraft && (
                              <FormCard
                                key={form.id}
                                isDraft={form.isDraft}
                                formName={form.formName}
                                formDescription={form.formData.description || ''}
                                id={form.id}
                                onDelete={() => handleDelete(form.id)}
                                onArchive={() => archiveForm(form.id)}
                                handleSend={() => handleSend(form.id)}
                                onAction={() => handleFormAction(form.id)} // Pass action handler
                              />
                            )}
                            {type === "Saved" && !form.isDraft && (
                              <FormCard
                                key={form.id}
                                isDraft={form.isDraft}
                                formName={form.formName}
                                formDescription={form.formData.description || ''}
                                id={form.id}
                                onDelete={() => handleDelete(form.id)}
                                onArchive={() => archiveForm(form.id)}
                                handleSend={() => handleSend(form.id)}
                                onAction={() => handleFormAction(form.id)} // Pass action handler
                              />
                            )}
                            {type === "All" && (
                              <FormCard
                                key={form.id}
                                isDraft={form.isDraft}
                                formName={form.formName}
                                formDescription={form.formData.description || ''}
                                id={form.id}
                                onDelete={() => handleDelete(form.id)}
                                onArchive={() => archiveForm(form.id)}
                                handleSend={() => handleSend(form.id)}
                                onAction={() => handleFormAction(form.id)} // Pass action handler
                              />
                            )}
                          </Container>
                        )
                      ))
                    )}
                  </>
                )}

                {navItem === 'Templates' && (
                  <>
                    {parsedTemplates.length === 0 ? (
                     <Container
                          style={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            marginTop: '270px'
                          }}
                        >
                          <p
                            style={{
                              fontSize: '1.5rem',
                              color: '#333',
                              fontWeight: 'bold',
                              lineHeight: '1.4',
                            }}
                          >
                            No templates available. <br />
                            Click the <span style={{ color: '#1ab394' }}> "+" </span> icon to start creating your templates.
                          </p>
                        </Container>

                    ) : (
                      parsedTemplates.map(template => (
                        !template.isTrash && !template.isArchive && (
                          <Container pos={'relative'} mt={'50'} key={template.id}>
                            {/* {type !== "Saved" && template.isDraft && (
                              <Badge
                                variant="filled"
                                pos={'absolute'}
                                left={'70%'}
                                top={'27%'}
                                styles={{ root: { zIndex: 100 } }}
                              >
                                Draft
                              </Badge>
                            )} */}
                            {type === "Draft" && template.isDraft && (
                              <FormCard
                                key={template.id}
                                isDraft={template.isDraft}
                                formName={template.TemplateName}
                                formDescription={template.TemplateData.description || ''}
                                id={template.id}
                                Template={true}
                                onDelete={() => handleTemplateDelete(template.id)}
                                onArchive={() => archiveTemplate(template.id)}
                              />
                            )}
                            {type === "Saved" && !template.isDraft && (
                              <FormCard
                                key={template.id}
                                isDraft={template.isDraft}
                                formName={template.TemplateName}
                                formDescription={template.TemplateData.description || ''}
                                id={template.id}
                                Template={true}
                                onDelete={() => handleTemplateDelete(template.id)}
                                onArchive={() => archiveTemplate(template.id)}
                              />
                            )}
                            {type === "All" && (
                              <FormCard
                                key={template.id}
                                isDraft={template.isDraft}
                                formName={template.TemplateName}
                                formDescription={template.TemplateData.description || ''}
                                id={template.id}
                                Template={true}
                                onDelete={() => handleTemplateDelete(template.id)}
                                onArchive={() => archiveTemplate(template.id)}
                              />
                            )}
                          </Container>
                        )
                      ))
                    )}
                  </>
                )}

                {navItem === 'Shared With Me' && (
                  <>
                  {sharedForms.length === 0 ? (
                    <Container
                          style={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            marginTop: '270px'
                          }}
                        >
                          <p
                            style={{
                              fontSize: '1.5rem',
                              color: '#333',
                              fontWeight: 'bold',
                              lineHeight: '1.4',
                            }}
                          >
                            No forms shared with you. <br />
                          </p>
                        </Container>
                  ) : (
                  Array.isArray(sharedForms) &&
                  sharedForms.map(form => (
                    <Container pos={'relative'} mt={'50'} key={form.id}>
                      <FormCard
                        {...form}
                        accessRole={form.accessRole}
                        formDescription={form.formData.description || ''}
                        shared
                      />
                    </Container>
                  )))}
                  </>
                )}


                {navItem === 'Archive' && (
                  <>
                    {parsedForms
                      .filter(form => form.isArchive && !form.isTrash)
                      .length === 0 && parsedTemplates
                      .filter(template => template.isArchive && !template.isTrash)
                      .length === 0 ? (
                      <Container 
                       style={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            marginTop: '270px'}}>

                        <p style={{
                          fontSize: '1.5rem',
                          color: '#333',
                          fontWeight: 'bold',
                          marginBottom: '1rem',
                          lineHeight: '1.4'
                        }}>
                          No archived forms or templates available.
                        </p>
                      </Container>
                    ) : (
                      <>
                        {parsedForms.map(form => (
                          form.isArchive && !form.isTrash && (
                            <Container pos={'relative'} mt={'50'} key={form.id}>
                              {/* {form.isDraft && (
                                <Badge
                                  variant="filled"
                                  pos={'absolute'}
                                  left={'70%'}
                                  top={'27%'}
                                  styles={{ root: { zIndex: 100 } }}
                                >
                                  Draft
                                </Badge>
                              )} */}
                              <FormCard
                                key={form.id}
                                isDraft={form.isDraft}
                                formName={form.formName}
                                formDescription={form.formData.description || ''}
                                id={form.id}
                                onDelete={() => handleDelete(form.id)}
                                offArchive={() => archiveOff(form.id)}
                                onAction={() => handleFormAction(form.id)} // Pass action handler
                              />
                            </Container>
                          )
                        ))}
                        {parsedTemplates.map(template => (
                          template.isArchive && !template.isTrash && (
                            <Container pos={'relative'} mt={'30'} key={template.id}>
                              {/* {template.isDraft && (
                                <Badge
                                  variant="filled"
                                  pos={'absolute'}
                                  left={'70%'}
                                  top={'27%'}
                                  styles={{ root: { zIndex: 100 } }}
                                >
                                  Draft
                                </Badge>
                              )} */}
                              <FormCard
                                key={template.id}
                                isDraft={template.isDraft}
                                formName={template.TemplateName}
                                formDescription={template.TemplateData.description || ''}
                                id={template.id}
                                Template={true}
                                onDelete={() => handleTemplateDelete(template.id)}
                                offArchive={() => archiveOffTemplate(template.id)}
                              />
                            </Container>
                          )
                        ))}
                      </>
                    )}
                  </>
                )}

                {navItem === 'Trash' && (
                  <>
                    {parsedForms
                      .filter(form => form.isTrash)
                      .length === 0 && parsedTemplates
                      .filter(template => template.isTrash)
                      .length === 0 ? (
                      <Container 
                       style={{
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            marginTop: '270px'}}>
                        <p style={{
                          fontSize: '1.5rem',
                          color: '#333',
                          fontWeight: 'bold',
                          marginBottom: '1rem',
                          lineHeight: '1.4'
                        }}>
                          No forms or templates in trash.
                        </p>
                      </Container>
                    ) : (
                      <>
                        {parsedForms.map(form => (
                          form.isTrash && (
                            <Container pos={'relative'} mt={'50'} key={form.id}>
                              <FormCard
                                key={form.id}
                                isDraft={form.isDraft}
                                formName={form.formName}
                                formDescription={form.formData.description || ''}
                                id={form.id}
                                Trash={true}
                                onDelete={() => handleDelete(form.id)}
                                handleRestore={() => handleRestore(form.id)}
                              />
                            </Container>
                          )
                        ))}
                        {parsedTemplates.map(template => (
                          template.isTrash && (
                            <Container pos={'relative'} mt={'50'} key={template.id}>
                              <FormCard
                                key={template.id}
                                isDraft={template.isDraft}
                                formName={template.TemplateName}
                                formDescription={template.TemplateData.description || ''}
                                id={template.id}
                                Template={true}
                                Trash={true}
                                onDelete={() => handleTemplateDelete(template.id)}
                                restoreTemplate={() => restoreTemplate(template.id)}
                              />
                            </Container>
                          )
                        ))}
                      </>
                    )}
                  </>
                )}
              </Flex>
            </ScrollArea>
          </Flex>

          <Modal
            scrollAreaComponent={ScrollArea.Autosize}
            opened={templateOpen}
            onClose={() => setTemplateOpen(false)}
            centered
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
            size="90%"
            styles={{
              modal: {
                zIndex: 10000,
                padding: '30px',
                overflow: 'visible',
                height: '70vh',
                maxHeight: '70vh',
              },
              body: {
                padding: '0',
                height: 'calc(70vh - 100px)',
                overflow: 'hidden',
              },
              header: {
                padding: '20px 30px',
                borderBottom: '1px solid #e0e0e0',
              },
            }}
            title={
              <Text size="xl" weight={700} style={{ fontSize: '24px', color: '#333' }}>
                {TextMessages.chooseAnTemplate}
              </Text>
            }
          >
            <ScrollArea style={{ height: '100%', width: '100%' }}>
              <Container fluid size="xl" style={{ padding: '30px' }}>
                {parsedTemplates.some(
                  (template) => !template.isDraft && !template.isTrash && !template.isArchive
                ) ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '20px',
                      alignItems: 'start',
                    }}
                  >
                    {parsedTemplates.map((template) => (
                      !template.isDraft &&
                      !template.isTrash &&
                      !template.isArchive && (
                        <FormCard
                          key={template.id}
                          formName={template.TemplateName}
                          formDescription={template.TemplateData.description || ''}
                          Template
                          TemplateForm
                          viewTemplate
                          createForm
                          id={template.id}
                          onDelete={() => handleTemplateDelete(template.id)}
                          onArchive={() => archiveTemplate(template.id)}
                        />
                      )
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '80px 0',
                      color: '#888',
                    }}
                  >
                    <Text size="lg" weight={500}>
                      No templates available
                    </Text>
                    <Text size="sm" color="dimmed" mt="xs">
                      Create a new template to get started.
                    </Text>
                  </div>
                )}
              </Container>
            </ScrollArea>
          </Modal>

          <Modal
            opened={modalOpen}
            onClose={handleCloseModal}
            title={TextMessages.chooseOption}
            centered
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
          >
            <Button onClick={handleStartFromScratch} fullWidth mb="md">
              {TextMessages.startFromScratch}
            </Button>
            <Button onClick={handleChooseTemplate} fullWidth>
              {TextMessages.templates}
            </Button>
          </Modal>

          <Modal
            opened={opened}
            onClose={close}
            title={TextMessages.formLink}
            centered
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
          >
            <p style={{ wordBreak: 'break-all', textAlign: 'center' }}>
                  <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#17a387ff' }}>
                    {link}
                  </a>
                </p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                    toast.success('Form link copied to clipboard!');
                  }}
                  fullWidth
                  style={{ marginTop: '15px' }}
                  variant='light'
                >
                  Copy Link
                </Button>
            <Button
              onClick={() => setEmailModalOpen(true)}
              fullWidth
              mt="md"
            >
              {TextMessages.sendViaEmail}
            </Button>
          </Modal>

          <Modal
            opened={emailModalOpen}
            onClose={() => setEmailModalOpen(false)}
            title="Send Form Link via Email"
            centered
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
          >
            <TextInput
              label="Recipient Email"
              placeholder="Enter email"
              value={recipientEmail}
              onChange={(event) => setRecipientEmail(event.currentTarget.value)}
              mb="md"
            />
            <Button
              onClick={() => sendMail(recipientEmail)}
              fullWidth
              loading={mailLoading}
            >
              Send Email
            </Button>
          </Modal>

          <FooterCentered />
          <ToastContainer />
        </div>
      )}
    </div>
  );
}
